/**
 * Region-aware fuel unit + currency helpers.
 * Detects US vs Canada from a state / province code or an address string,
 * then converts between US gallons ↔ litres, MPG ↔ km/L, and formats
 * currency in USD or CAD.
 *
 * Storage stays in US units (gallons, USD) because that's what our vendor
 * sheets and driver receipts report — we convert ONLY at the display layer.
 */

const CANADIAN_PROVINCES = new Set([
  "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT",
]);

// Conversion factors
const GAL_TO_L = 3.78541;           // 1 US gallon = 3.78541 litres
const MPG_TO_KMPL = 0.425144;        // 1 MPG = 0.425144 km/L
// FX: rough placeholder — receipts already come in CAD if driver was in Canada.
// We do NOT apply an FX conversion; we only relabel the currency prefix based
// on where the transaction happened. Admin toggles override if needed.

export function isCanadianState(state) {
  if (!state) return false;
  return CANADIAN_PROVINCES.has(String(state).toUpperCase().trim());
}

export function regionFromAddress(address) {
  if (!address) return null;
  const s = ` ${String(address).toUpperCase()} `;
  // Look for a Canadian province token surrounded by non-letters
  for (const prov of CANADIAN_PROVINCES) {
    if (new RegExp(`[^A-Z]${prov}[^A-Z]`).test(s)) return "CA";
  }
  if (/CANADA/.test(s)) return "CA";
  return "US";
}

/** Returns 'CA' | 'US' from a row that may carry a state OR a location_address. */
export function detectRegion({ state, location_address, address } = {}) {
  if (isCanadianState(state)) return "CA";
  const addr = location_address || address;
  if (addr) return regionFromAddress(addr);
  return "US";
}

/**
 * Returns 'CA' | 'US' from a truck's resolved unit system. Priority:
 *   1. truck.effective_unit_system (server-resolved from unit_system + company)
 *   2. truck.unit_system
 * Metric → 'CA', imperial (or unknown) → 'US'.
 * Use this for volume, distance, and economy formatting so a Canadian truck
 * always shows L / km / km-per-L even when fueled up in the US.
 */
export function unitSystemFromTruck(truck) {
  if (!truck) return "US";
  const u = String(truck.effective_unit_system || truck.unit_system || "").toLowerCase();
  if (u === "metric") return "CA";
  if (u === "imperial") return "US";
  return "US";
}

// ---- Formatters (region-aware) ----

export function formatVolume(gallonsValue, region = "US", digits = 1) {
  if (gallonsValue == null || Number.isNaN(gallonsValue)) return "—";
  if (region === "CA") {
    return `${(Number(gallonsValue) * GAL_TO_L).toFixed(digits)} L`;
  }
  return `${Number(gallonsValue).toFixed(digits)} gal`;
}

/** Convert stored miles → km when the truck is metric. */
export function formatDistance(milesValue, region = "US", digits = 0) {
  if (milesValue == null || Number.isNaN(milesValue)) return "—";
  if (region === "CA") {
    return `${(Number(milesValue) * 1.60934).toFixed(digits)} km`;
  }
  return `${Number(milesValue).toFixed(digits)} mi`;
}

export function formatCurrency(amount, region = "US", digits = 2) {
  if (amount == null || Number.isNaN(amount)) return "—";
  const prefix = region === "CA" ? "CA$" : "US$";
  return `${prefix}${Number(amount).toFixed(digits)}`;
}

/**
 * Price per unit. `unitRegion` controls the volume unit (L vs gal); optional
 * `currencyRegion` controls the currency prefix so a Canadian truck fueling in
 * the US shows US$/L (unit follows the truck, currency follows the receipt).
 * Backwards compatible: when currencyRegion is null, both fall back to region.
 */
export function formatPricePerUnit(costPerGallon, region = "US", digits = 3, currencyRegion = null) {
  if (costPerGallon == null || Number.isNaN(costPerGallon)) return "—";
  const curRegion = currencyRegion || region;
  const prefix = curRegion === "CA" ? "CA$" : "US$";
  const val = region === "CA" ? Number(costPerGallon) / GAL_TO_L : Number(costPerGallon);
  return `${prefix}${val.toFixed(digits)}/${region === "CA" ? "L" : "gal"}`;
}

/** MPG in US, km/L in Canada. */
export function formatEconomy(mpg, region = "US") {
  if (mpg == null || Number.isNaN(mpg)) return "—";
  if (region === "CA") {
    return `${(Number(mpg) * MPG_TO_KMPL).toFixed(2)} km/L`;
  }
  return `${Number(mpg).toFixed(2)} MPG`;
}

export const UNITS = { GAL_TO_L, MPG_TO_KMPL };
